import { useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { CircleOff, Edit3, Eye, Plus, RefreshCw, Save, Trash2 } from 'lucide-react'
import {
  AdvancedDisclosure,
  Button,
  Checkbox,
  CodeBlock,
  CodeField,
  ConfirmDialog,
  Drawer,
  FormField,
  FormRow,
  FormSection,
  HelpTip,
  ProductPageShell,
  StatePanel,
  StatusBadge,
} from '@/components/common'
import { DecisionReview } from '@/components/patterns'
import { useOrganization } from '@/contexts/OrganizationContext'
import { igamingApi } from '@/services/api'
import { CollapsibleFieldGroup } from './shared/CollapsibleFieldGroup'
import {
  DEFAULT_FORM,
  DEFAULT_PLACEMENT_BY_TYPE,
  FIELD_HELP,
  JACKPOT_BEHAVIOR_OPTIONS,
  JACKPOT_TYPE_OPTIONS,
  PAGE_COPY,
  PAYOUT_OPTIONS,
  RULE_KIND_CONFIG,
  RULE_TYPES,
  SORT_STRATEGY_OPTIONS,
  TONE_OPTIONS,
  VOLATILITY_OPTIONS,
} from './shared/ruleConstants'
import { formatPlacement, formatRuleType, toIsoDateTime } from './shared/formatters'
import { buildConfig, buildFilterLogic, buildSortLogic, ruleToForm } from './shared/ruleHelpers'
import { buildPayloadChangeItems } from '@/utils/payloadReview'
import styles from './shared/igaming.module.css'

const RULE_REVIEW_FIELDS = {
  rule_type: { label: 'Rule kind', format: (value, { missing }) => missing ? 'Not configured' : formatRuleType(value), impact: 'Changes rule behavior and available targeting' },
  label: { label: 'Label', impact: 'Changes the operator-facing rule identity' },
  title: { label: 'Description', impact: 'Changes supporting player-facing context' },
  query: { label: 'Search phrase', impact: 'Changes the search request used to find games' },
  placement: { label: 'Placement', format: (value, { missing }) => missing ? 'Not configured' : formatPlacement(value), impact: 'Changes where players can encounter the rule' },
  sort_order: { label: 'Priority', impact: 'Changes ordering when active rules compete' },
  market: { label: 'Regulation', impact: 'Changes regulation targeting' },
  segment: { label: 'Segment', impact: 'Changes player segment targeting' },
  is_active: { label: 'Status', format: (value, { missing }) => missing ? 'Not configured' : value ? 'Active' : 'Inactive', impact: 'Controls runtime eligibility' },
  starts_at: { label: 'Starts', impact: 'Changes when the rule becomes eligible' },
  ends_at: { label: 'Ends', impact: 'Changes when the rule stops being eligible' },
}

function buildRulePayload(form) {
  return {
    rule_type: form.rule_type,
    label: form.label.trim(),
    title: form.title.trim() || null,
    query: form.query.trim(),
    placement: form.placement,
    sort_order: Number(form.sort_order) || 0,
    market: form.market.trim() || null,
    segment: form.segment.trim() || null,
    is_active: Boolean(form.is_active),
    filter_logic: buildFilterLogic(form),
    sort_logic: buildSortLogic(form),
    config: buildConfig(form),
    starts_at: toIsoDateTime(form.starts_at),
    ends_at: toIsoDateTime(form.ends_at),
  }
}

function IGamingRulesPage() {
  const { currentOrg, hasProductAccess } = useOrganization()
  const slug = currentOrg?.slug
  const queryClient = useQueryClient()
  const [form, setForm] = useState(DEFAULT_FORM)
  const [formError, setFormError] = useState('')
  const [previewResult, setPreviewResult] = useState(null)
  const [deleteCandidate, setDeleteCandidate] = useState(null)
  const [editorOpen, setEditorOpen] = useState(false)
  const [reviewOpen, setReviewOpen] = useState(false)
  const formRef = useRef(null)

  const canConfigure = hasProductAccess('igaming_search', 'configure')
  const isEditing = Boolean(form.id)
  const pageCopy = PAGE_COPY.rules

  const rulesQuery = useQuery({
    queryKey: ['igaming', slug, 'rules'],
    queryFn: () => igamingApi.listRules(slug),
    enabled: Boolean(slug),
  })

  const runtimeQuery = useQuery({
    queryKey: ['igaming', slug, 'runtime'],
    queryFn: () => igamingApi.getRuntimeConfig(slug),
    enabled: Boolean(slug),
  })

  const kindConfig = RULE_KIND_CONFIG[form.rule_type] || RULE_KIND_CONFIG.quick_pill

  const resetForm = () => {
    setForm(DEFAULT_FORM)
    setFormError('')
    setPreviewResult(null)
    setReviewOpen(false)
  }

  const removeRuleFromCache = async (ruleId) => {
    const removeFromPayload = (current) => {
      if (!current?.rules) return current
      return {
        ...current,
        rules: current.rules.filter((rule) => rule.id !== ruleId),
      }
    }

    await Promise.all([
      queryClient.cancelQueries({ queryKey: ['igaming', slug, 'rules'] }),
      queryClient.cancelQueries({ queryKey: ['igaming', slug, 'runtime'] }),
    ])
    queryClient.setQueryData(['igaming', slug, 'rules'], removeFromPayload)
    queryClient.setQueryData(['igaming', slug, 'runtime'], removeFromPayload)
  }

  const createRule = useMutation({
    mutationFn: (payload) => igamingApi.createRule(slug, payload),
    onSuccess: () => {
      toast.success('iGaming rule created')
      resetForm()
      setEditorOpen(false)
      queryClient.invalidateQueries({ queryKey: ['igaming', slug] })
    },
    onError: (error) => {
      setFormError(error.response?.data?.error || error.message || 'Failed to create rule')
    },
  })

  const updateRule = useMutation({
    mutationFn: ({ id, payload }) => igamingApi.updateRule(slug, id, payload),
    onSuccess: () => {
      toast.success('iGaming rule updated')
      resetForm()
      setEditorOpen(false)
      queryClient.invalidateQueries({ queryKey: ['igaming', slug] })
    },
    onError: (error) => {
      setFormError(error.response?.data?.error || error.message || 'Failed to update rule')
    },
  })

  const disableRule = useMutation({
    mutationFn: (id) => igamingApi.disableRule(slug, id),
    onSuccess: () => {
      toast.success('iGaming rule disabled')
      queryClient.invalidateQueries({ queryKey: ['igaming', slug] })
    },
  })

  const deleteRule = useMutation({
    mutationFn: (rule) => igamingApi.deleteRule(slug, rule.id),
    onSuccess: async (_, rule) => {
      toast.success('iGaming rule deleted')
      await removeRuleFromCache(rule.id)
      if (form.id === rule.id) {
        resetForm()
      }
      setDeleteCandidate(null)
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || error.message || 'Failed to delete rule')
    },
  })

  const previewRule = useMutation({
    mutationFn: (payload) => igamingApi.previewRule(slug, payload),
    onSuccess: (result) => {
      setPreviewResult(result)
      setFormError('')
    },
    onError: (error) => {
      setFormError(error.response?.data?.error || error.message || 'Failed to preview rule')
    },
  })

  const rules = useMemo(() => rulesQuery.data?.rules || [], [rulesQuery.data?.rules])

  const sortedRules = useMemo(() => {
    return [...rules].sort((a, b) => {
      if (a.is_active !== b.is_active) return a.is_active ? -1 : 1
      return (a.sort_order || 0) - (b.sort_order || 0)
    })
  }, [rules])

  const currentRule = useMemo(
    () => rules.find((rule) => rule.id === form.id) || null,
    [form.id, rules],
  )

  let ruleProposal = null
  let reviewPayloadError = ''
  try {
    ruleProposal = buildRulePayload(form)
  } catch (error) {
    reviewPayloadError = `Advanced JSON is invalid: ${error.message}`
  }
  const currentRulePayload = currentRule ? buildRulePayload(ruleToForm(currentRule)) : null
  const reviewChanges = ruleProposal ? buildPayloadChangeItems({
    current: currentRulePayload,
    proposed: ruleProposal,
    fieldMetadata: RULE_REVIEW_FIELDS,
    defaultImpact: 'Changes rule matching or runtime presentation',
  }) : []

  const updateForm = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }))
    setReviewOpen(false)
    setPreviewResult(null)
  }

  const handleRuleTypeChange = (event) => {
    const ruleType = event.target.value
    setForm((current) => ({
      ...DEFAULT_FORM,
      id: current.id,
      label: current.label,
      title: current.title,
      market: current.market,
      segment: current.segment,
      sort_order: current.sort_order,
      is_active: current.is_active,
      rule_type: ruleType,
      placement: DEFAULT_PLACEMENT_BY_TYPE[ruleType] || DEFAULT_FORM.placement,
    }))
    setReviewOpen(false)
    setPreviewResult(null)
  }

  const handleEditRule = (rule) => {
    setForm(ruleToForm(rule))
    setFormError('')
    setPreviewResult(null)
    setReviewOpen(false)
    setEditorOpen(true)
  }

  const handleSubmitRule = (event) => {
    event.preventDefault()
    try {
      const payload = buildRulePayload(form)
      if (isEditing) {
        updateRule.mutate({ id: form.id, payload })
      } else {
        createRule.mutate(payload)
      }
    } catch (error) {
      setFormError(`Advanced JSON is invalid: ${error.message}`)
    }
  }

  const handlePreviewRule = () => {
    try {
      previewRule.mutate(buildRulePayload(form))
    } catch (error) {
      setFormError(`Advanced JSON is invalid: ${error.message}`)
    }
  }

  return (
    <ProductPageShell
      title={pageCopy.title}
      subtitle={pageCopy.subtitle}
      actions={(
        <>
          <Button variant="secondary" onClick={() => {
            rulesQuery.refetch()
            runtimeQuery.refetch()
          }}>
            <RefreshCw size={16} />
            Refresh
          </Button>
          {canConfigure ? (
            <Button onClick={() => {
              resetForm()
              setEditorOpen(true)
            }}>
              <Plus size={16} />
              Create rule
            </Button>
          ) : null}
        </>
      )}
    >
      <div className={styles.stack}>
        {!canConfigure ? (
          <StatePanel
            state="restricted"
            title="Read-only iGaming rules"
            description="Configure access is required to create, edit, disable, or delete rules."
            compact
          />
        ) : null}
        <FormSection title="Configured rules">
          {runtimeQuery.isError ? (
            <StatePanel
              state="error"
              title="Runtime preview is unavailable"
              description={runtimeQuery.error?.response?.data?.error || runtimeQuery.error?.message}
              onRetry={() => runtimeQuery.refetch()}
              compact
            />
          ) : null}
          {rulesQuery.isLoading ? (
            <StatePanel state="loading" title="Loading iGaming rules" compact />
          ) : rulesQuery.isError ? (
            <StatePanel
              state="error"
              title="iGaming rules failed"
              description={rulesQuery.error?.response?.data?.error || rulesQuery.error?.message}
              onRetry={() => rulesQuery.refetch()}
              compact
            />
          ) : !sortedRules.length ? (
            <StatePanel state="empty" title="No iGaming rules" compact />
          ) : (
            <div className={styles.ruleList}>
              {sortedRules.map((rule) => (
                <article key={rule.id} className={styles.ruleItem}>
                  <div className={styles.ruleMain}>
                    <div className={styles.ruleTitleRow}>
                      <strong>{rule.label}</strong>
                      <StatusBadge type={rule.is_active ? 'success' : 'default'} size="small">
                        {rule.is_active ? 'Active' : 'Inactive'}
                      </StatusBadge>
                    </div>
                    <div className={styles.ruleMeta}>
                      <span>{formatRuleType(rule.rule_type)}</span>
                      <span>{formatPlacement(rule.placement)}</span>
                      <span>Priority {rule.sort_order}</span>
                      {rule.market ? <span>{rule.market}</span> : null}
                    </div>
                    {rule.query ? <p className={styles.ruleQuery}>{rule.query}</p> : null}
                  </div>
                  {canConfigure && (
                    <div className={styles.ruleActions}>
                      <Button
                        variant="secondary"
                        aria-label={`Edit ${rule.label}`}
                        onClick={() => handleEditRule(rule)}
                      >
                        <Edit3 size={15} />
                        Edit
                      </Button>
                      {rule.is_active && (
                        <Button
                          variant="ghost"
                          aria-label={`Disable ${rule.label}`}
                          disabled={disableRule.isPending}
                          onClick={() => disableRule.mutate(rule.id)}
                        >
                          <CircleOff size={15} />
                          Disable
                        </Button>
                      )}
                      <Button
                        variant="danger"
                        aria-label={`Delete ${rule.label}`}
                        disabled={deleteRule.isPending}
                        onClick={() => setDeleteCandidate(rule)}
                      >
                        <Trash2 size={15} />
                        Delete
                      </Button>
                    </div>
                  )}
                </article>
              ))}
            </div>
          )}
        </FormSection>
        <Drawer
          open={editorOpen}
          side="right"
          title={isEditing ? 'Edit rule' : 'Create rule'}
          description="Set the rule behavior, then review its runtime effect before saving."
          footer={reviewOpen ? (
            <>
              <Button type="button" variant="secondary" onClick={() => setReviewOpen(false)}>
                Back
              </Button>
              <Button type="button" variant="secondary" disabled={previewRule.isPending} onClick={handlePreviewRule}>
                <Eye size={16} />
                {previewRule.isPending ? 'Previewing...' : 'Preview'}
              </Button>
              <Button type="submit" form="igaming-rule-form" disabled={createRule.isPending || updateRule.isPending}>
                {isEditing ? <Save size={16} /> : <Plus size={16} />}
                {createRule.isPending || updateRule.isPending
                  ? 'Saving...'
                  : isEditing ? 'Save rule' : 'Create rule'}
              </Button>
            </>
          ) : (
            <>
              <Button type="button" variant="secondary" disabled={previewRule.isPending} onClick={handlePreviewRule}>
                <Eye size={16} />
                {previewRule.isPending ? 'Previewing...' : 'Preview'}
              </Button>
              <Button type="button" onClick={() => setReviewOpen(true)} disabled={Boolean(reviewPayloadError)}>
                Review rule
              </Button>
            </>
          )}
          onClose={() => {
            setEditorOpen(false)
            resetForm()
          }}
        >
          <FormSection title={reviewOpen ? 'Review' : 'Rule details'}>
            <form id="igaming-rule-form" ref={formRef} className={styles.form} onSubmit={handleSubmitRule}>
              <div hidden={reviewOpen}>
                <div className={styles.fieldGroup}>
                <div className={styles.groupTitle}>Rule</div>
                <FormRow>
                  <FormField
                    label="Rule kind"
                    labelExtra={<HelpTip text="Choose the operator task first. The fields below change to match the selected rule." label="Rule kind help" />}
                    type="select"
                    value={form.rule_type}
                    options={RULE_TYPES}
                    onChange={handleRuleTypeChange}
                  />
                  <Checkbox
                    label="Active"
                    checked={form.is_active}
                    onChange={(event) => updateForm('is_active', event.target.checked)}
                  />
                </FormRow>
                <FormRow>
                  <FormField
                    label={kindConfig.label}
                    required
                    value={form.label}
                    placeholder={kindConfig.labelPlaceholder}
                    onChange={(event) => updateForm('label', event.target.value)}
                  />
                  {kindConfig.showQuery ? (
                    <FormField
                      label={kindConfig.queryLabel}
                      labelExtra={<HelpTip text={FIELD_HELP.searchPhrase} label={`${kindConfig.queryLabel} help`} />}
                      value={form.query}
                      placeholder={kindConfig.queryPlaceholder}
                      onChange={(event) => updateForm('query', event.target.value)}
                    />
                  ) : (
                    <FormField
                      label="Description"
                      value={form.title}
                      placeholder="Optional note for this rule"
                      onChange={(event) => updateForm('title', event.target.value)}
                    />
                  )}
                </FormRow>
                {kindConfig.showQuery && (
                  <FormField
                    label="Description"
                    value={form.title}
                    placeholder="Optional note or tile description"
                    onChange={(event) => updateForm('title', event.target.value)}
                  />
                )}
              </div>

              <CollapsibleFieldGroup title="Targeting">
                <FormRow>
                  <FormField
                    label="Regulation"
                    labelExtra={<HelpTip text={FIELD_HELP.market} label="Regulation help" />}
                    value={form.market}
                    placeholder="Regulation code, e.g. NJ"
                    onChange={(event) => updateForm('market', event.target.value)}
                  />
                  <FormField
                    label="Segment"
                    labelExtra={<HelpTip text={FIELD_HELP.segment} label="Segment help" />}
                    value={form.segment}
                    placeholder="Segment key, e.g. vip"
                    onChange={(event) => updateForm('segment', event.target.value)}
                  />
                </FormRow>
                <FormRow>
                  <FormField
                    label="Priority"
                    labelExtra={<HelpTip text="Lower numbers win when multiple active rules compete for the same position." label="Priority help" />}
                    type="number"
                    value={form.sort_order}
                    onChange={(event) => updateForm('sort_order', event.target.value)}
                  />
                  {kindConfig.showPlacementChoice ? (
                    <FormField
                      label="Behavior"
                      type="select"
                      value={form.placement}
                      options={JACKPOT_BEHAVIOR_OPTIONS}
                      onChange={(event) => updateForm('placement', event.target.value)}
                    />
                  ) : kindConfig.showAfterRows ? (
                    <FormField
                      label="Show after rows"
                      labelExtra={<HelpTip text="Controls when the mid-page strip appears after players scroll past lobby rows." label="Show after rows help" />}
                      type="number"
                      min="1"
                      value={form.after_rows}
                      onChange={(event) => updateForm('after_rows', event.target.value)}
                    />
                  ) : kindConfig.showTone ? (
                    <FormField
                      label="Color"
                      type="select"
                      value={form.tone}
                      options={TONE_OPTIONS}
                      onChange={(event) => updateForm('tone', event.target.value)}
                    />
                  ) : (
                    <div />
                  )}
                </FormRow>
              </CollapsibleFieldGroup>

              {kindConfig.showSchedule && (
                <div className={styles.fieldGroup}>
                  <div className={styles.groupTitle}>Timing</div>
                  <FormRow>
                    <FormField
                      label="Starts"
                      type="datetime-local"
                      value={form.starts_at}
                      onChange={(event) => updateForm('starts_at', event.target.value)}
                    />
                    <FormField
                      label="Ends"
                      type="datetime-local"
                      value={form.ends_at}
                      onChange={(event) => updateForm('ends_at', event.target.value)}
                    />
                  </FormRow>
                </div>
              )}

              {kindConfig.showFilters && (
                <CollapsibleFieldGroup title="Filters">
                  <FormRow>
                    <FormField
                      label="Provider"
                      labelExtra={<HelpTip text={FIELD_HELP.provider} label="Provider help" />}
                      value={form.provider}
                      placeholder="One provider, e.g. Pragmatic Play"
                      onChange={(event) => updateForm('provider', event.target.value)}
                    />
                    <FormField
                      label="Category"
                      labelExtra={<HelpTip text={FIELD_HELP.category} label="Category help" />}
                      value={form.category}
                      placeholder="One category, e.g. Live Dealer"
                      onChange={(event) => updateForm('category', event.target.value)}
                    />
                  </FormRow>
                  <FormRow>
                    <FormField
                      label="Volatility"
                      type="select"
                      value={form.volatility}
                      options={VOLATILITY_OPTIONS}
                      onChange={(event) => updateForm('volatility', event.target.value)}
                    />
                    <FormField
                      label="Payout signal"
                      type="select"
                      value={form.payout_level}
                      options={PAYOUT_OPTIONS}
                      onChange={(event) => updateForm('payout_level', event.target.value)}
                    />
                  </FormRow>
                  <FormRow>
                    <FormField
                      label="Themes"
                      labelExtra={<HelpTip text={FIELD_HELP.themes} label="Themes help" />}
                      value={form.theme_tags}
                      placeholder="Comma-separated, e.g. irish, jackpot, ancient egypt"
                      onChange={(event) => updateForm('theme_tags', event.target.value)}
                    />
                    <FormField
                      label="Max win from"
                      labelExtra={<HelpTip text="Use the win multiplier threshold, such as 50000 for 50,000x." label="Max win help" />}
                      type="number"
                      value={form.max_win_min}
                      placeholder="10000"
                      onChange={(event) => updateForm('max_win_min', event.target.value)}
                    />
                  </FormRow>
                  {kindConfig.showSortStrategy && (
                    <FormRow>
                      <FormField
                        label="Sort by"
                        type="select"
                        value={form.sort_strategy}
                        options={SORT_STRATEGY_OPTIONS}
                        onChange={(event) => updateForm('sort_strategy', event.target.value)}
                      />
                      <div />
                    </FormRow>
                  )}
                </CollapsibleFieldGroup>
              )}

              {kindConfig.showProvider && (
                <div className={styles.fieldGroup}>
                  <div className={styles.groupTitle}>Boost target</div>
                  <FormField
                    label="Provider"
                    labelExtra={<HelpTip text={FIELD_HELP.provider} label="Provider help" />}
                    required
                    value={form.provider}
                    placeholder="One provider, e.g. Pragmatic Play"
                    onChange={(event) => updateForm('provider', event.target.value)}
                  />
                </div>
              )}

              {kindConfig.showGameCodes && (
                <div className={styles.fieldGroup}>
                  <div className={styles.groupTitle}>Boost target</div>
                  <FormField
                    label="Game codes"
                    labelExtra={<HelpTip text="Enter one or more platform game codes, separated by commas." label="Game codes help" />}
                    required
                    value={form.game_codes}
                    placeholder="pop_5d336d18_igt, max-megaways"
                    onChange={(event) => updateForm('game_codes', event.target.value)}
                  />
                </div>
              )}

              {kindConfig.showPromo && (
                <div className={styles.fieldGroup}>
                  <div className={styles.groupTitle}>Promotion</div>
                  <FormField
                    label="Promo type"
                    value={form.promo_type}
                    placeholder="game_of_week, tournament, free_spins"
                    onChange={(event) => updateForm('promo_type', event.target.value)}
                  />
                </div>
              )}

              {kindConfig.showJackpot && (
                <div className={styles.fieldGroup}>
                  <div className={styles.groupTitle}>Jackpot</div>
                  <FormRow>
                    <FormField
                      label="Jackpot type"
                      type="select"
                      value={form.jackpot_type}
                      options={JACKPOT_TYPE_OPTIONS}
                      onChange={(event) => updateForm('jackpot_type', event.target.value)}
                    />
                    {kindConfig.showTone && (
                      <FormField
                        label="Color"
                        type="select"
                        value={form.tone}
                        options={TONE_OPTIONS}
                        onChange={(event) => updateForm('tone', event.target.value)}
                      />
                    )}
                    {!kindConfig.showTone && <div />}
                  </FormRow>
                </div>
              )}

              {kindConfig.showWeekday && (
                <div className={styles.fieldGroup}>
                  <div className={styles.groupTitle}>Daypart</div>
                  <FormRow>
                    <FormField
                      label="Days"
                      value={form.weekday_days}
                      placeholder="Monday, Tuesday, Wednesday"
                      onChange={(event) => updateForm('weekday_days', event.target.value)}
                    />
                    <FormField
                      label="Starts"
                      value={form.time_start}
                      placeholder="09:00"
                      onChange={(event) => updateForm('time_start', event.target.value)}
                    />
                  </FormRow>
                  <FormRow>
                    <FormField
                      label="Ends"
                      value={form.time_end}
                      placeholder="17:00"
                      onChange={(event) => updateForm('time_end', event.target.value)}
                    />
                    <div />
                  </FormRow>
                </div>
              )}

              {kindConfig.showBoost && (
                <div className={styles.fieldGroup}>
                  <div className={styles.groupTitle}>Boost</div>
                  <FormRow>
                    <FormField
                      label="Boost strength"
                      labelExtra={<HelpTip text="Higher values move matching games above otherwise similar results." label="Boost strength help" />}
                      type="number"
                      min="0"
                      step="0.1"
                      value={form.boost_strength}
                      onChange={(event) => updateForm('boost_strength', event.target.value)}
                    />
                    <div />
                  </FormRow>
                </div>
              )}

              <AdvancedDisclosure label="Advanced JSON">
                <div className={styles.advancedFields}>
                    <CodeField
                      label="Advanced filters"
                      help="Use only for metadata filters that are not available in the fields above."
                      rows={8}
                      value={form.filter_logic}
                      onChange={(value) => updateForm('filter_logic', value)}
                    />
                    <CodeField
                      label="Advanced sorting"
                      rows={4}
                      value={form.sort_logic}
                      onChange={(value) => updateForm('sort_logic', value)}
                    />
                    <CodeField
                      label="Advanced display options"
                      rows={5}
                      value={form.config}
                      onChange={(value) => updateForm('config', value)}
                    />
                </div>
              </AdvancedDisclosure>
              </div>

              {!reviewOpen ? (
                formError || reviewPayloadError ? (
                  <StatePanel state="error" title={formError || reviewPayloadError} compact />
                ) : null
              ) : (
              <DecisionReview
                title={isEditing ? 'Review rule changes' : 'Review new rule'}
                description="Confirm the targeting and runtime effect before saving. Previewing does not change the active configuration."
                contextItems={[
                  { label: 'Rule kind', value: formatRuleType(form.rule_type) },
                  { label: 'Placement', value: formatPlacement(form.placement) },
                  { label: 'Priority', value: String(Number(form.sort_order) || 0) },
                ]}
                changeItems={reviewChanges}
                consequence={previewResult ? (
                  <div className={styles.previewPanel}>
                    <div className={styles.previewHeader}>
                      <strong>Preview</strong>
                      <span>{previewResult.estimated_result_count ?? 0} local matches</span>
                    </div>
                    {previewResult.warnings?.length ? (
                      <ul className={styles.warningList}>
                        {previewResult.warnings.map((warning) => <li key={warning}>{warning}</li>)}
                      </ul>
                    ) : null}
                    <AdvancedDisclosure label="Advanced preview request">
                      <CodeBlock
                        value={previewResult.search_params}
                        label="Preview request"
                        language="json"
                      />
                    </AdvancedDisclosure>
                  </div>
                ) : 'Saving an active rule makes it eligible for the next runtime configuration refresh.'}
                error={formError || reviewPayloadError}
                actions={null}
              />
              )}
            </form>
          </FormSection>
        </Drawer>

      </div>
      <ConfirmDialog
        open={Boolean(deleteCandidate)}
        title="Delete rule"
        message={`Delete "${deleteCandidate?.label || 'this rule'}"? This permanently removes it from Wager Studio and from runtime config.`}
        confirmLabel={deleteRule.isPending ? 'Deleting...' : 'Delete rule'}
        cancelLabel="Keep rule"
        onConfirm={() => {
          if (deleteCandidate && !deleteRule.isPending) {
            deleteRule.mutate(deleteCandidate)
          }
        }}
        onCancel={() => {
          if (!deleteRule.isPending) {
            setDeleteCandidate(null)
          }
        }}
        isDestructive
      />
    </ProductPageShell>
  )
}

export default IGamingRulesPage
